import { jsx } from "react/jsx-runtime";
import { useMelwaysInfo } from "@seek/melways-react";
import { createContext, useState, useMemo, useContext } from "react";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import { UAParser } from "ua-parser-js";
const defaultOptions = {};
var ExperimentDeviceType = /* @__PURE__ */ ((ExperimentDeviceType2) => {
  ExperimentDeviceType2["PHONE"] = "PHONE";
  ExperimentDeviceType2["TABLET"] = "TABLET";
  return ExperimentDeviceType2;
})(ExperimentDeviceType || {});
var Platform = /* @__PURE__ */ ((Platform2) => {
  Platform2["ANDROID"] = "ANDROID";
  Platform2["IOS"] = "IOS";
  Platform2["WEB"] = "WEB";
  return Platform2;
})(Platform || {});
const FeatureFlagsDocument = gql`
    query FeatureFlags($flags: [String!]!, $visitorContext: VisitorContextInput, $experienceContext: ExperienceContextInput, $applicationContext: ApplicationContextInput, $deviceContext: DeviceContextInput) {
  featureFlags(
    flags: $flags
    visitorContext: $visitorContext
    experienceContext: $experienceContext
    applicationContext: $applicationContext
    deviceContext: $deviceContext
  ) {
    id
    key
    value
    variation
    reason
    error
  }
}
    `;
function useFeatureFlagsQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(FeatureFlagsDocument, options);
}
const getDefaultUserAgent = () => {
  var _a;
  return typeof window !== "undefined" ? (_a = window.navigator) == null ? void 0 : _a.userAgent : "";
};
const isGoogleBot = (ua) => ua.toLowerCase().includes("google");
const isDatadogSyntheticTest = (ua) => /Datadog|Synthetics/.test(ua);
const checkIsBotVisit = (userAgent = getDefaultUserAgent()) => isGoogleBot(userAgent) || isDatadogSyntheticTest(userAgent);
const botVisitDefaultValue = (flags) => flags.map((flag) => ({
  id: flag,
  key: flag,
  value: null,
  variation: null,
  reason: "BOT_VISIT"
}));
const getFeatureFlagsContextData = (data, featureFlags) => {
  var _a;
  return (_a = data == null ? void 0 : data.featureFlags) == null ? void 0 : _a.filter(
    (flag) => !flag.error && featureFlags.includes(flag.key)
  );
};
const getExperimentsContextData = (data, experimentFlags) => {
  var _a;
  return (_a = data == null ? void 0 : data.featureFlags) == null ? void 0 : _a.filter(
    (flag) => !flag.error && experimentFlags.includes(flag.key)
  );
};
const getDeviceInfo = (userAgent = getDefaultUserAgent()) => {
  const { device, os } = UAParser(userAgent || "");
  let type = null;
  const userAgentDeviceType = device == null ? void 0 : device.type;
  switch (userAgentDeviceType == null ? void 0 : userAgentDeviceType.toLowerCase()) {
    case "mobile":
      type = ExperimentDeviceType.PHONE;
      break;
    case "tablet":
      type = ExperimentDeviceType.TABLET;
      break;
    default:
      type = null;
  }
  return {
    manufacturer: (device == null ? void 0 : device.vendor) || null,
    model: (device == null ? void 0 : device.model) || null,
    os: (os == null ? void 0 : os.name) && (os == null ? void 0 : os.version) && {
      name: os.name,
      version: os.version
    } || null,
    type
  };
};
const enrolmentReasonList = ["FALLTHROUGH", "TARGET_MATCH", "RULE_MATCH"];
const isEnrolledInExperiment = ({ reason }) => enrolmentReasonList.includes(reason);
const FeatureFlags = createContext({});
const FeatureFlagProvider = ({
  featureFlags,
  experimentsKeysAndTrackingNumbers,
  customContext,
  debug = false,
  userAgent,
  solId,
  children
}) => {
  const { language, locale, country, brand } = useMelwaysInfo();
  const [featureFlagsContext, setFeatureFlagsContext] = useState({
    isExperimentsReady: false
  });
  const experimentFlags = useMemo(
    () => experimentsKeysAndTrackingNumbers.map((experiment) => experiment.key),
    [experimentsKeysAndTrackingNumbers]
  );
  const flagsList = useMemo(
    () => [...featureFlags, ...experimentFlags],
    [featureFlags, experimentFlags]
  );
  const isBotVisit = useMemo(() => checkIsBotVisit(userAgent), [userAgent]);
  const deviceInfo = useMemo(() => getDeviceInfo(userAgent), [userAgent]);
  useFeatureFlagsQuery({
    variables: {
      flags: flagsList,
      visitorContext: { key: solId },
      deviceContext: {
        key: solId,
        locale,
        country,
        ...deviceInfo
      },
      experienceContext: {
        key: solId,
        brand,
        country,
        language
      },
      ...customContext,
      applicationContext: {
        key: solId,
        ...customContext == null ? void 0 : customContext.applicationContext,
        // Always forcing the platform to be Web for web applications
        channel: Platform.WEB
      }
    },
    skip: isBotVisit || flagsList.length === 0,
    onCompleted: (data) => {
      const experiments = isBotVisit ? botVisitDefaultValue(experimentFlags) : getExperimentsContextData(data, experimentFlags);
      setFeatureFlagsContext({
        featureFlags: getFeatureFlagsContextData(data, featureFlags),
        experiments,
        experimentsKeysAndTrackingNumbers,
        enrolledExperiments: experiments ? experiments.filter(
          (experiment) => isEnrolledInExperiment(experiment)
        ) : [],
        isExperimentsReady: true
      });
    },
    onError: () => {
      setFeatureFlagsContext({
        isExperimentsReady: true
      });
    }
  });
  if (debug) {
    console.log({
      isBotVisit,
      featureFlags: featureFlagsContext.featureFlags
    });
  }
  return /* @__PURE__ */ jsx(FeatureFlags.Provider, { value: featureFlagsContext, children });
};
const toExperimentKeys = (keys) => isSingleExperiment(keys) ? keys.toLowerCase() : keys.map((k) => k.toLowerCase());
const isSingleExperiment = (key) => !Array.isArray(key);
const getExperimentsTrackingNumbersMap = (keys, experimentsKeysAndTrackingNumbers) => keys.reduce((allExperiments, k) => {
  const key = toExperimentKeys(k);
  const { trackingNumber } = (experimentsKeysAndTrackingNumbers == null ? void 0 : experimentsKeysAndTrackingNumbers.find(
    (experiment) => toExperimentKeys(experiment.key) === key
  )) ?? {};
  if (trackingNumber) {
    allExperiments[key] = trackingNumber;
  }
  return allExperiments;
}, {});
const useExperimentsEligibleProps = () => {
  const { experimentsKeysAndTrackingNumbers, enrolledExperiments } = useContext(FeatureFlags);
  const experimentsKeyTrackingNumberMap = useMemo(
    () => getExperimentsTrackingNumbersMap(
      (enrolledExperiments == null ? void 0 : enrolledExperiments.map((flag) => flag.key)) ?? [],
      experimentsKeysAndTrackingNumbers
    ),
    [enrolledExperiments, experimentsKeysAndTrackingNumbers]
  );
  const tealiumEligibleProps = useMemo(
    () => (enrolledExperiments == null ? void 0 : enrolledExperiments.map((experimentItem) => ({
      id: experimentItem.key,
      variant: experimentItem.variation ?? null,
      num: experimentsKeyTrackingNumberMap[experimentItem.key] ?? null
    }))) || [],
    [enrolledExperiments, experimentsKeyTrackingNumberMap]
  );
  const HubbleEligibleProps = useMemo(
    () => (enrolledExperiments == null ? void 0 : enrolledExperiments.map((experimentItem) => ({
      id: experimentItem.key
    }))) || [],
    [enrolledExperiments]
  );
  return { tealiumEligibleProps, HubbleEligibleProps };
};
const useExperimentsParticipatedProps = (keys) => {
  const { experimentsKeysAndTrackingNumbers, enrolledExperiments } = useContext(FeatureFlags);
  const experiments = useMemo(
    () => (enrolledExperiments == null ? void 0 : enrolledExperiments.filter(
      (flag) => toExperimentKeys(keys).includes(toExperimentKeys(flag.key))
    )) || [],
    [enrolledExperiments, keys]
  );
  const experimentTrackingNumbers = useMemo(
    () => getExperimentsTrackingNumbersMap(keys, experimentsKeysAndTrackingNumbers),
    [experimentsKeysAndTrackingNumbers, keys]
  );
  const tealiumExperimentsParticipatedProps = useMemo(
    () => ({
      experimentsParticipated: experiments.map(({ key, variation }) => ({
        id: key,
        variant: variation ?? null,
        num: experimentTrackingNumbers[key] ?? null
      }))
    }),
    [experiments, experimentTrackingNumbers]
  );
  const hubbleExperimentsParticipatedProps = useMemo(
    () => ({
      experiments: experiments.reduce(
        (allData, { key, variation }) => {
          allData[key] = {
            name: key,
            participantStatus: "participating",
            variant: variation ?? null
          };
          return allData;
        },
        {}
      )
    }),
    [experiments]
  );
  return {
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  };
};
const useIsExperimentsReady = () => {
  const { isExperimentsReady } = useContext(FeatureFlags);
  return isExperimentsReady;
};
const useExperiment = (key) => {
  const { experiments } = useContext(FeatureFlags);
  const experimentData = useMemo(
    () => (experiments == null ? void 0 : experiments.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    )) ?? null,
    [experiments, key]
  );
  const {
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  } = useExperimentsParticipatedProps([key]);
  if (!experimentData) {
    return {
      experimentData: null,
      tealiumExperimentsParticipatedProps: null,
      hubbleExperimentsParticipatedProps: null
    };
  }
  return {
    experimentData,
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  };
};
const useExperimentVariation = (key) => {
  const {
    experimentData,
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  } = useExperiment(key);
  return {
    variation: (experimentData == null ? void 0 : experimentData.variation) ?? null,
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  };
};
const useExperimentValue = (key) => {
  const {
    experimentData,
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  } = useExperiment(key);
  return {
    value: (experimentData == null ? void 0 : experimentData.value) ?? null,
    tealiumExperimentsParticipatedProps,
    hubbleExperimentsParticipatedProps
  };
};
const useFeatureFlag = (key) => {
  const { featureFlags } = useContext(FeatureFlags);
  const featureFlagData = useMemo(
    () => (featureFlags == null ? void 0 : featureFlags.find(
      (flag) => toExperimentKeys(flag.key) === toExperimentKeys(key)
    )) ?? null,
    [featureFlags, key]
  );
  return (featureFlagData == null ? void 0 : featureFlagData.value) ?? null;
};
export {
  FeatureFlagProvider,
  useExperiment,
  useExperimentValue,
  useExperimentVariation,
  useExperimentsEligibleProps,
  useExperimentsParticipatedProps,
  useFeatureFlag,
  useIsExperimentsReady
};
